/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';

import type { RequirementBusy } from 'hooks/useProposalDocuments';
import { downloadFile } from 'services/actions/conlicitaseg_actions';
import type {
  ProposalGuaranteeModalidade,
  ProposalGuaranteeResponse,
} from 'services/actions/ai_management_proposal_documents';
import {
  STATUS_LABEL_MAP,
  formatCurrency,
  formatDate,
  formatInsuranceName,
} from 'services/conlicitaseg';

interface AiManagementProposalGuaranteeBodyProps {
  guarantee: ProposalGuaranteeResponse;
  locked: boolean;
  busy?: RequirementBusy;
  hasFiles: boolean;
  // Mesma regra do botão de cotação da licitação (useGuaranteeInsurance).
  canQuote: boolean;
  onUpload: (file: File, modalidade: ProposalGuaranteeModalidade) => void;
  onConfirm: () => void;
  // Abre o painel lateral do ConLicitaSeg; a etapa relê a Garantia ao fechar.
  onOpenQuote: () => void;
  // Pedido do painel (erro ao carregar → "Enviar documento"): abre a escolha da modalidade.
  sendDocumentRequest?: number;
}

export const GUARANTEE_MODALIDADES: { valor: ProposalGuaranteeModalidade; rotulo: string }[] = [
  { valor: 'caucao', rotulo: 'Caução' },
  { valor: 'fianca', rotulo: 'Fiança bancária' },
  { valor: 'seguro', rotulo: 'Seguro de outra seguradora' },
];

const ACCEPT = '.pdf,.docx';

const idDoLink = (link: string | null | undefined) => {
  const match = (link ?? '').match(/[?&]id=(\d+)/);
  return match ? Number(match[1]) : null;
};

const moeda = (valor: number | null | undefined) =>
  valor === null || valor === undefined ? '-' : formatCurrency(valor) || '-';

// Corpo da Garantia: o estado vem do back, que lê o ConLicitaSeg a cada consulta da etapa.
const AiManagementProposalGuaranteeBody: React.FC<AiManagementProposalGuaranteeBodyProps> = ({
  guarantee,
  locked,
  busy,
  hasFiles,
  canQuote,
  onUpload,
  onConfirm,
  onOpenQuote,
  sendDocumentRequest = 0,
}) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const [escolhendo, setEscolhendo] = useState(false);
  const [modalidade, setModalidade] = useState<ProposalGuaranteeModalidade | null>(
    guarantee.modalidade,
  );
  const [downloadError, setDownloadError] = useState<string | null>(null);
  useEffect(() => {
    if (sendDocumentRequest > 0) setEscolhendo(true);
  }, [sendDocumentRequest]);

  const abrirCotacao = onOpenQuote;

  const baixar = (link: string | null | undefined, tipo: 'apolice' | 'cotacao') => {
    const id = idDoLink(link);
    if (!id) return;
    setDownloadError(null);
    downloadFile(id, tipo).catch(() => setDownloadError('Não foi possível baixar o arquivo.'));
  };

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file && modalidade) {
      onUpload(file, modalidade);
      setEscolhendo(false);
    }
  };

  const working = Boolean(busy);

  const botaoConfirmar = (
    <button
      type="button"
      className="btn btn-primary mr-2 mb-2"
      disabled={working}
      onClick={onConfirm}
    >
      {busy === 'confirm' ? 'Confirmando…' : 'Confirmar garantia'}
    </button>
  );

  const renderEnvio = (rotulo: string) =>
    escolhendo ? (
      <div className="card p-3 mb-2 w-100">
        <p className="mb-2">
          <strong>Qual a modalidade da garantia?</strong>
        </p>
        {GUARANTEE_MODALIDADES.map((opcao) => (
          <label key={opcao.valor} className="d-block mb-1" htmlFor={`garantia-${opcao.valor}`}>
            <input
              id={`garantia-${opcao.valor}`}
              type="radio"
              name="garantia-modalidade"
              className="mr-2"
              checked={modalidade === opcao.valor}
              onChange={() => setModalidade(opcao.valor)}
            />
            {opcao.rotulo}
          </label>
        ))}
        <div className="mt-2">
          <button
            type="button"
            className="btn btn-primary btn-sm mr-2"
            disabled={!modalidade || working}
            onClick={() => fileInput.current?.click()}
          >
            {busy === 'upload' ? 'Enviando…' : 'Escolher arquivo'}
          </button>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={() => setEscolhendo(false)}
          >
            Cancelar
          </button>
        </div>
      </div>
    ) : (
      <button
        type="button"
        className="btn btn-outline-secondary mr-2 mb-2"
        disabled={working}
        onClick={() => setEscolhendo(true)}
      >
        {rotulo}
      </button>
    );

  const renderEstado = () => {
    const { apolice, cotacao } = guarantee;
    switch (guarantee.estado) {
      case 'apolice_importada':
        return (
          <>
            <p className="mb-1">
              <strong>Apólice importada do ConLicitaSeg</strong>
            </p>
            <ul className="list-unstyled mb-2">
              <li>Número: {apolice?.numero ?? '-'}</li>
              <li>Seguradora: {formatInsuranceName(apolice?.seguradora ?? '')}</li>
              <li>Valor segurado: {moeda(apolice?.valor_segurado)}</li>
              <li>
                Vigência: {formatDate(apolice?.vigencia_inicio ?? undefined)} a{' '}
                {formatDate(apolice?.vigencia_fim ?? undefined)}
              </li>
              <li>Prêmio: {moeda(apolice?.premio)}</li>
            </ul>
            <div className="d-flex flex-wrap align-items-center">
              <button
                type="button"
                className="btn btn-outline-primary mr-2 mb-2"
                onClick={() => baixar(apolice?.link_pdf, 'apolice')}
              >
                Ver apólice
              </button>
              {!locked && botaoConfirmar}
            </div>
          </>
        );
      case 'cotacao_concluida':
        return (
          <>
            <p className="mb-1">
              <strong>Cotação feita no ConLicitaSeg</strong>
            </p>
            <ul className="list-unstyled mb-2">
              <li>Seguradora: {formatInsuranceName(cotacao?.seguradora ?? '')}</li>
              <li>Prêmio: {moeda(cotacao?.premio)}</li>
              <li>
                Situação:{' '}
                {STATUS_LABEL_MAP[cotacao?.status as keyof typeof STATUS_LABEL_MAP] ??
                  cotacao?.status ??
                  '-'}
              </li>
            </ul>
            <p className="text-muted small mb-2">
              A Garantia fica pendente até a apólice ser emitida ou você enviar um documento.
            </p>
            {!locked && (
              <div className="d-flex flex-wrap align-items-center">
                <button
                  type="button"
                  className="btn btn-outline-primary mr-2 mb-2"
                  onClick={() => baixar(cotacao?.link_pdf, 'cotacao')}
                >
                  Ver cotação
                </button>
                {canQuote && (
                  <button
                    type="button"
                    className="btn btn-primary mr-2 mb-2"
                    onClick={abrirCotacao}
                  >
                    Continuar no ConLicitaSeg
                  </button>
                )}
                {renderEnvio('Enviar documento')}
              </div>
            )}
          </>
        );
      case 'documento_enviado': {
        const rotulo = GUARANTEE_MODALIDADES.find((m) => m.valor === guarantee.modalidade)?.rotulo;
        return (
          <>
            <p className="mb-2">
              <strong>Garantia enviada como documento</strong>
              {rotulo && <span className="text-muted"> · {rotulo}</span>}
            </p>
            {!locked && (
              <div className="d-flex flex-wrap align-items-center">
                {renderEnvio('Enviar outro arquivo')}
                {hasFiles && botaoConfirmar}
              </div>
            )}
          </>
        );
      }
      default:
        return (
          <>
            <p className="mb-2">Nenhuma apólice encontrada no ConLicitaSeg para esta licitação.</p>
            {!locked && (
              <div className="d-flex flex-wrap align-items-center">
                {canQuote && (
                  <button
                    type="button"
                    className="btn btn-primary mr-2 mb-2"
                    onClick={abrirCotacao}
                  >
                    Fazer minha cotação
                  </button>
                )}
                {renderEnvio('Enviar documento')}
              </div>
            )}
          </>
        );
    }
  };

  return (
    <div className="mb-2">
      <input ref={fileInput} type="file" accept={ACCEPT} className="d-none" onChange={handleFile} />
      {guarantee.valor_exigido && (
        <p className="mb-2">
          <strong>Valor exigido:</strong> {guarantee.valor_exigido}
        </p>
      )}
      {guarantee.aviso && <div className="alert alert-warning py-2 mb-2">{guarantee.aviso}</div>}
      {renderEstado()}
      {downloadError && <div className="text-danger small">{downloadError}</div>}
    </div>
  );
};

export default AiManagementProposalGuaranteeBody;
